import React, { memo, useMemo } from 'react'
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { format } from 'date-fns'
import ItineraryReportHeaderPdf from '../sentinel/itinerary/pdf/ItineraryReportHeaderPdf.component'
import ItineraryReportFooterPdf from '../sentinel/itinerary/pdf/ItineraryReportFooterPdf.component'
import { maskCpf } from '~/prix/utils/cpf'
import { secondaryTones } from '~/design'
import { AgentReportFilter, AgentReportInitialMetricCard } from './types/agentReport.types'

const SECONDARY_AGENT_COLOR = secondaryTones[1]

Font.register({
  family: 'Figtree',
  fonts: [
    {
      src: '/fonts/Figtree-Regular.ttf',
      fontWeight: 400,
    },
    {
      src: '/fonts/Figtree-Bold.ttf',
      fontWeight: 700,
    },
  ],
})

const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 65,
    paddingHorizontal: 40,
    backgroundColor: '#ffffff',
    fontFamily: 'Figtree',
  },

  title: {
    fontSize: 20,
    color: '#0F438A',
    marginBottom: 8,
  },

  subtitle: {
    fontSize: 10,
    color: '#4b5563',
    marginBottom: 12,
  },

  
  summaryGrid: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 15,
  },

  summaryInfoGroup: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    gap: 10,
  },

  summaryLogoContainer: {
    width: 200,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
  },

  summaryLogo: {
    width: 120,
    height: 42,
    objectFit: 'contain',
  },

  summaryColumn: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },

  summaryCard: {
    backgroundColor: '#F5F5F5',
    borderRadius: 6,
    padding: 12,
  },

  summaryLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 4,
  },

  summaryValue: {
    fontSize: 18,
    color: '#0F438A',
    fontFamily: 'Figtree',
    fontWeight: 700,
  },

   mapTitle: {
    fontSize: 14,
    color: '#0F438A',
    fontFamily: 'Figtree',
    fontWeight: 700,
    marginBottom: 10,
  },


  filterText: {
    fontSize: 10,
    color: '#111827',
    marginBottom: 3,
  },

  imageFrame: {
    marginTop: 0,
    width: '100%',
    height: 560,
    backgroundColor: '#ffffff',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'top center',
  },

  compactGrid: {
    marginTop: 10,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },

  compactItemHalf: {
    width: '49%',
    minHeight: 110,
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
  },

  compactItemFull: {
    width: '100%',
    minHeight: 110,
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
  },

  compactCardTitle: {
    fontSize: 11,
    color: '#666',
    marginBottom: 4,
  },

  compactCardLine: {
    fontSize: 18,
    color: '#0F438A',
    fontFamily: 'Figtree',
    fontWeight: 700,
  },

  mapTitleSecondary: {
    color: SECONDARY_AGENT_COLOR,
  },


  productivityImageFrame: {
    marginTop: 0,
    width: '100%',
    height: 560,
    backgroundColor: '#ffffff',
    marginBottom: 10,
  },

  productivityImage: {
    width: '100%',
    height: '80%',
    objectFit: 'contain',
    objectPosition: 'top top',
  },

  cardImageTitle: {
    fontSize: 12,
    color: '#0F438A',
    fontFamily: 'Figtree',
    fontWeight: 700,
    marginBottom: 0,
  },

  footer: {
    marginTop: 8,
    fontSize: 9,
    color: '#6b7280',
    textAlign: 'center',
  },
})

interface AgentReportImage {
  type: 'image'
  src: string
  width: number
  height: number
  cardTitle: string
}

interface AgentReportPdfProps {
  generatedAt: Date
  title: string
  filters: AgentReportFilter
  images: AgentReportImage[]
  summaryCards: AgentReportInitialMetricCard[]
}

interface CardImageLayoutRule {
  matcher: RegExp
  frameHeight: number
  imageFit: 'contain' | 'cover'
  imagePosition: 'top' | 'center'
  imageScale: number
  centerInFrame: boolean
  imageOffsetX: string
  imageOffsetY: string
}

const DEFAULT_CARD_IMAGE_LAYOUT: Omit<CardImageLayoutRule, 'matcher'> = {
  frameHeight: 560,
  imageFit: 'contain',
  imagePosition: 'top',
  imageScale: 1,
  centerInFrame: false,
  imageOffsetX: '0%',
  imageOffsetY: '0%',
}

const CARD_IMAGE_LAYOUT_RULES: CardImageLayoutRule[] = [
  {
    matcher: /gr[aá]fico de produtividade/i,
    frameHeight: 560,
    imageFit: 'contain',
    imagePosition: 'top',
    imageScale: 1.2,
    centerInFrame: false,
    imageOffsetX: '0%',
    imageOffsetY: '0%',
  },
  {
    matcher: /mapa do agente/i,
    frameHeight: 560,
    imageFit: 'contain',
    imagePosition: 'top',
    imageScale: 1,
    centerInFrame: false,
    imageOffsetX: '0%',
    imageOffsetY: '0%',
  },
  {
    matcher: /matriz\s+ou\s+filial/i,
    frameHeight: 520,
    imageFit: 'cover',
    imagePosition: 'center',
    imageScale: 1.6,
    centerInFrame: true,
    imageOffsetX: '0%',
    imageOffsetY: '0%',
  },
  {
    matcher: /gr[aá]fico/i,
    frameHeight: 896,
    imageFit: 'cover',
    imagePosition: 'center',
    imageScale: 2,
    centerInFrame: true,
    imageOffsetX: '0%',
    imageOffsetY: '0%',
  }
]

function AgentReportPdf({ generatedAt, title, filters, images, summaryCards }: AgentReportPdfProps) {
  const PRODUCTIVITY_FRAME_HEIGHT = 250
  const PRODUCTIVITY_FRAME_WIDTH = '100%'
  const PRODUCTIVITY_PAGE_MAX_CONTENT_HEIGHT = 610
  const PRODUCTIVITY_CARD_TITLE_HEIGHT = 24
  const PRODUCTIVITY_CARD_GAP = 4
  const DETAIL_PAGE_MAX_CONTENT_HEIGHT = 570
  const DETAIL_CARD_TITLE_HEIGHT = 24
  const DETAIL_CARD_SPACING_HEIGHT = 12
  const DETAIL_CONTENT_WIDTH = 515
  const DETAIL_MAX_FRAME_HEIGHT =
    DETAIL_PAGE_MAX_CONTENT_HEIGHT - DETAIL_CARD_TITLE_HEIGHT - DETAIL_CARD_SPACING_HEIGHT
  const DETAIL_COMPARISON_FRAME_HEIGHT = Math.floor(
    (DETAIL_PAGE_MAX_CONTENT_HEIGHT - DETAIL_CARD_TITLE_HEIGHT * 2 - 4) / 2,
  )
  const MATRIX_SINGLE_AGENT_FRAME_HEIGHT = 272
  const MATRIX_COMPARISON_FRAME_HEIGHT = 248
  const MATRIX_COMPARISON_CARD_SPACING_HEIGHT = 4

  const isProductivityImage = (imageItem: AgentReportImage) => {
    return /gr[aá]fico de produtividade/i.test(imageItem.cardTitle || '')
  }

  const isMapAgentCard = (imageItem: AgentReportImage) => {
    return /mapa do agente/i.test(imageItem.cardTitle || '')
  }

  const isMatrixCard = (imageItem: AgentReportImage) => {
    return /matriz\s+ou\s+filial/i.test(imageItem.cardTitle || '')
  }

  const getCardImageLayout = (cardTitle: string) => {
    const foundRule = CARD_IMAGE_LAYOUT_RULES.find(cardImageLayoutRule =>
      cardImageLayoutRule.matcher.test(cardTitle || ''),
    )

    if (foundRule) {
      return foundRule
    }

    return DEFAULT_CARD_IMAGE_LAYOUT
  }

  const getMetricLineValue = (line: string) => {
    const separatorIndex = line.indexOf(':')
    if (separatorIndex === -1) return line.trim()

    return line.slice(separatorIndex + 1).trim()
  }

  const productivityImages = useMemo(() => images.filter(isProductivityImage), [images])
  const nonProductivityImages = useMemo(() => images.filter(imageItem => !isProductivityImage(imageItem)), [images])

  const firstCompactPageCards = useMemo(() => summaryCards.slice(0, 5), [summaryCards])
  const hasCompactFirstPage = firstCompactPageCards.length > 0
  const pagedImages = useMemo(
    () => (hasCompactFirstPage ? nonProductivityImages.slice(5) : nonProductivityImages),
    [hasCompactFirstPage, nonProductivityImages],
  )
  const hasComparisonAttendant = !!filters.secondaryAttendant

  const productivityCardPages = useMemo(() => {
    interface PreparedProductivityCard {
      imageItem: AgentReportImage
      frameHeight: number
      requiredHeight: number
    }

    const preparedCards: PreparedProductivityCard[] = productivityImages.map(imageItem => {
      const frameHeight = PRODUCTIVITY_FRAME_HEIGHT

      return {
        imageItem,
        frameHeight,
        requiredHeight: frameHeight + PRODUCTIVITY_CARD_TITLE_HEIGHT,
      }
    })

    type PageAccumulator = {
      pages: PreparedProductivityCard[][]
      currentPage: PreparedProductivityCard[]
      currentHeight: number
    }

    if (!hasComparisonAttendant) {
      return preparedCards.map(preparedCard => [preparedCard])
    }

    const finalAccumulator = preparedCards.reduce<PageAccumulator>((accumulator, preparedCard) => {
      const nextHeight =
        accumulator.currentHeight +
        (accumulator.currentPage.length > 0 ? PRODUCTIVITY_CARD_GAP : 0) +
        preparedCard.requiredHeight

      if (nextHeight <= PRODUCTIVITY_PAGE_MAX_CONTENT_HEIGHT) {
        accumulator.currentPage.push(preparedCard)
        accumulator.currentHeight = nextHeight
        return accumulator
      }

      if (accumulator.currentPage.length > 0) {
        accumulator.pages.push(accumulator.currentPage)
      }

      accumulator.currentPage = [preparedCard]
      accumulator.currentHeight = preparedCard.requiredHeight
      return accumulator
    }, {
      pages: [],
      currentPage: [],
      currentHeight: 0,
    })

    if (finalAccumulator.currentPage.length > 0) {
      finalAccumulator.pages.push(finalAccumulator.currentPage)
    }

    return finalAccumulator.pages
  }, [productivityImages, hasComparisonAttendant])

  const detailCardPages = useMemo(() => {
    interface PreparedDetailCard {
      imageItem: AgentReportImage
      frameHeight: number
      requiredHeight: number
      isMapCard: boolean
      isMatrixCard: boolean
      isComparisonMatrixCard: boolean
      matrixImageScale: number
      hasTitle: boolean
      normalizedTitle: string
    }

    const preparedCards = pagedImages.map(imageItem => {
      const originalTitle = imageItem.cardTitle || 'Gráfico'
      const normalizedTitle = originalTitle.replace(/\s*\(\d+\)\s*$/, '')
      const matrixTitleMatch = originalTitle.match(/\((\d+)\)\s*$/)
      const matrixCardIndex = matrixTitleMatch ? Number(matrixTitleMatch[1]) : null
      const hasTitle = !isMatrixCard(imageItem) || !matrixCardIndex || matrixCardIndex === 1
      const isMapCard = isMapAgentCard(imageItem)
      const isComparisonMatrixCard = isMatrixCard(imageItem) && !!matrixCardIndex
      const matrixImageScale = isMatrixCard(imageItem) && !matrixCardIndex ? 1.6 : 0.9
      const hasValidDimensions = imageItem.width > 0 && imageItem.height > 0
      const baseFrameHeight = hasValidDimensions
        ? Math.round((imageItem.height / imageItem.width) * DETAIL_CONTENT_WIDTH)
        : DETAIL_CONTENT_WIDTH
      const defaultSafeFrameHeight = Math.min(baseFrameHeight, DETAIL_MAX_FRAME_HEIGHT)
      const matrixFrameHeight = isComparisonMatrixCard
        ? MATRIX_COMPARISON_FRAME_HEIGHT
        : MATRIX_SINGLE_AGENT_FRAME_HEIGHT
      const frameHeight = isMatrixCard(imageItem)
        ? matrixFrameHeight
        : hasComparisonAttendant && !isMapCard
          ? Math.min(defaultSafeFrameHeight, DETAIL_COMPARISON_FRAME_HEIGHT)
          : defaultSafeFrameHeight
      const cardSpacingHeight = isComparisonMatrixCard
        ? MATRIX_COMPARISON_CARD_SPACING_HEIGHT
        : hasComparisonAttendant
          ? 0
        : DETAIL_CARD_SPACING_HEIGHT
      const requiredHeight =
        frameHeight + (hasTitle ? DETAIL_CARD_TITLE_HEIGHT : 0) + cardSpacingHeight

      return {
        imageItem,
        frameHeight,
        requiredHeight,
        isMapCard,
        isMatrixCard: isMatrixCard(imageItem),
        isComparisonMatrixCard,
        matrixImageScale,
        hasTitle,
        normalizedTitle,
      }
    })

    type PageAccumulator = {
      pages: PreparedDetailCard[][]
      currentPage: PreparedDetailCard[]
      currentHeight: number
    }

    const finalAccumulator = preparedCards.reduce<PageAccumulator>((accumulator, preparedCard) => {
      if (preparedCard.isMapCard) {
        if (accumulator.currentPage.length > 0) {
          accumulator.pages.push(accumulator.currentPage)
          accumulator.currentPage = []
          accumulator.currentHeight = 0
        }

        accumulator.pages.push([preparedCard])
        return accumulator
      }

      const canFitCurrentPage =
        accumulator.currentPage.length > 0 &&
        accumulator.currentHeight + preparedCard.requiredHeight <= DETAIL_PAGE_MAX_CONTENT_HEIGHT

      if (canFitCurrentPage) {
        accumulator.currentPage.push(preparedCard)
        accumulator.currentHeight += preparedCard.requiredHeight
        return accumulator
      }

      if (accumulator.currentPage.length > 0) {
        accumulator.pages.push(accumulator.currentPage)
      }

      accumulator.currentPage = [preparedCard]
      accumulator.currentHeight = preparedCard.requiredHeight
      return accumulator
    }, {
      pages: [],
      currentPage: [],
      currentHeight: 0,
    })

    if (finalAccumulator.currentPage.length > 0) {
      finalAccumulator.pages.push(finalAccumulator.currentPage)
    }

    const normalizedPages = [...finalAccumulator.pages]
    const lastPage = normalizedPages[normalizedPages.length - 1]

    if (
      !hasComparisonAttendant &&
      lastPage &&
      lastPage.length > 1 &&
      !lastPage.some(detailCard => detailCard.isMatrixCard)
    ) {
      const lastChart = lastPage[lastPage.length - 1]
      const pageWithoutLastChart = lastPage.slice(0, -1)

      normalizedPages[normalizedPages.length - 1] = pageWithoutLastChart
      normalizedPages.push([lastChart])
    }

    return normalizedPages
  }, [pagedImages, hasComparisonAttendant])

  return (
    <Document>
      {hasCompactFirstPage && (
        <Page key='report-page-compact-1' size='A4' style={styles.page}>
          <ItineraryReportHeaderPdf title={title} />
          <>  
            <View style={styles.summaryGrid}>
              <View style={styles.summaryInfoGroup}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.summaryLabel}>Agente:</Text>
                  <Text style={styles.mapTitle}>{filters.attendant?.name || 'Não informado'}</Text>
                </View>

                {filters.secondaryAttendant && (
                  <View style={{ flex: 1 }}>
                    <Text style={styles.summaryLabel}>Agente comparado:</Text>
                    <Text style={[styles.mapTitle, styles.mapTitleSecondary]}>
                      {filters.secondaryAttendant.name || 'Não informado'}
                    </Text>
                  </View>
                )}
              </View>

              <View style={styles.summaryLogoContainer}>
                <Image src='/sebrae-logo-app.png' style={styles.summaryLogo} />
              </View>
            </View>
            <View>
              <Text style={styles.summaryLabel}>Período:</Text>
              <Text style={styles.mapTitle}>{filters.dateRange || 'Não informado'}</Text>
            </View>
          </>
          
          <View style={styles.compactGrid}>
            {firstCompactPageCards.map((summaryCard, summaryCardIndex) => (
              <View
                key={`compact-card-${summaryCardIndex}`}
                style={summaryCardIndex < 4 ? styles.compactItemHalf : styles.compactItemFull}
              >
                <Text style={styles.compactCardTitle}>{summaryCard.title}</Text>
                {summaryCard.lines.map((line, lineIndex) => (
                  <Text
                    key={`compact-card-line-${summaryCardIndex}-${lineIndex}`}
                    style={[
                      styles.compactCardLine,
                      hasComparisonAttendant && lineIndex === 1 ? styles.mapTitleSecondary : {},
                    ]}
                  >
                    {getMetricLineValue(line)}
                  </Text>
                ))}
              </View>
            ))}
          </View>

          <ItineraryReportFooterPdf generatedAt={generatedAt} />
        </Page>
      )}

      {productivityCardPages.map((productivityCardPage, pageIndex) => (
        <Page key={`report-page-productivity-${pageIndex}`} size='A4' style={styles.page}>
          <ItineraryReportHeaderPdf title={title} />

          <View style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
            {productivityCardPage.map((productivityCard, cardIndex) => (
              <View
                key={`report-page-productivity-card-${pageIndex}-${cardIndex}`}
                wrap={false}
                style={{
                  width: '100%',
                  marginBottom: cardIndex < productivityCardPage.length - 1 ? PRODUCTIVITY_CARD_GAP : 0,
                }}
              >
                <Text style={styles.cardImageTitle}>{productivityCard.imageItem.cardTitle || 'Gráfico'}</Text>

                <View
                  style={[
                    styles.productivityImageFrame,
                    {
                      height: productivityCard.frameHeight,
                      width: PRODUCTIVITY_FRAME_WIDTH,
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      overflow: 'hidden',
                    },
                  ]}
                > 
                  <Image
                    src={productivityCard.imageItem.src}
                    style={styles.productivityImage}
                  />
                </View>
              </View>
            ))}
          </View>

          <ItineraryReportFooterPdf generatedAt={generatedAt} />
        </Page>
      ))}

      {detailCardPages.map((detailCardPage, pageIndex) => (
        <Page key={`report-page-detail-${pageIndex}`} size='A4' style={styles.page}>
          <ItineraryReportHeaderPdf title={title} />

          <View style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
            {detailCardPage.map((detailCard, cardIndex) => {
              const hasTwoChartsOnPage = detailCardPage.length === 2
              const spacingBetweenCharts = hasTwoChartsOnPage ? 4 : detailCard.isComparisonMatrixCard ? 4 : 8
              const shouldApplyBottomSpacing = cardIndex < detailCardPage.length - 1
              const bottomSpacing = shouldApplyBottomSpacing ? spacingBetweenCharts : 0
              const chartBlockHeight = hasTwoChartsOnPage
                ? Math.max(
                    Math.floor((DETAIL_PAGE_MAX_CONTENT_HEIGHT - spacingBetweenCharts) / 2),
                    1,
                  )
                : undefined

              const chartFrameHeight = detailCard.frameHeight

              return (
                <View
                  key={`report-page-detail-card-${pageIndex}-${cardIndex}`}
                  wrap={false}
                  style={{
                    width: '100%',
                    marginBottom: bottomSpacing,
                    ...(chartBlockHeight ? { height: chartBlockHeight } : {}),
                  }}
                >
                  {detailCard.hasTitle && (
                    <Text style={styles.cardImageTitle}>{detailCard.normalizedTitle}</Text>
                  )}

                  <View
                    style={
                      detailCard.isMatrixCard
                        ? [
                            styles.imageFrame,
                            {
                              height: chartFrameHeight,
                              width: '100%',
                              alignSelf: 'center',
                              marginLeft: 'auto',
                              marginRight: 'auto',
                              justifyContent: 'center',
                              alignItems: 'center',
                              overflow: 'hidden',
                            },
                          ]
                        : [
                            styles.imageFrame,
                            {
                              height: chartFrameHeight,
                              overflow: 'hidden',
                            },
                          ]
                    }
                  >
                    <Image
                      src={detailCard.imageItem.src}
                      style={
                        detailCard.isMatrixCard
                          ? [
                              styles.image,
                              {
                                objectPosition: 'center center',
                                width: `${Math.round(detailCard.matrixImageScale * 100)}%`,
                                height: `${Math.round(detailCard.matrixImageScale * 100)}%`,
                              },
                            ]
                          : styles.image
                      }
                    />
                  </View>
                </View>
              )
            })}
          </View>

          <ItineraryReportFooterPdf generatedAt={generatedAt} />
        </Page>
      ))}
    </Document>
  )
}

export default memo(AgentReportPdf)
